import { router, usePage } from '@inertiajs/react'
import {
  Sidebar,
  SidebarHeader,
  SidebarBody,
  SidebarFooter,
  SidebarSection,
  SidebarHeading,
} from '@/components/ui'
import { Logo } from '@/components/brand/Logo'
import { useTranslations } from '@/lib/i18n'
import { SidebarNavItem } from './SidebarNavItem'
import { MerchantSwitcher } from './MerchantSwitcher'
import { UserMenu } from './UserMenu'
import { NAVIGATION, visibleItems } from './navigation'
import type { SharedProps } from '@/types'

/**
 * The Collectia-Blue sidebar (DESIGN_GUIDELINES §6): white logo, optional
 * merchant switcher, role-filtered nav, and the account menu in the footer.
 * Rendered inside a `dark` scope so Catalyst's structural borders use the
 * light-on-dark variants. Switcher + account menu appear only when signed in
 * (auth lands in Phase 2).
 */
export function AppSidebar() {
  const { props, url } = usePage<SharedProps>()
  const { auth, branding } = props
  const { t } = useTranslations()
  const role = auth.user?.role ?? null

  const isCurrent = (href: string) => url === href || url.startsWith(`${href}/`)

  return (
    <Sidebar className="dark bg-brand-blue">
      <SidebarHeader>
        <div className="px-2 py-1.5">
          <Logo variant="white" src={branding.logo_white} className="h-8" alt={branding.brand_name} />
        </div>
        {auth.user && (
          <MerchantSwitcher
            merchants={auth.merchants}
            current={auth.current_merchant}
            emptyLabel={t('common.no_merchant')}
            onSelect={(id) => router.post('/current_merchant', { merchant_id: id })}
          />
        )}
      </SidebarHeader>

      <SidebarBody>
        {NAVIGATION.map((group, i) => {
          const items = visibleItems(group.items, role)
          if (items.length === 0) return null
          return (
            <SidebarSection key={group.headingKey ?? i}>
              {group.headingKey && <SidebarHeading>{t(group.headingKey)}</SidebarHeading>}
              {items.map((item) => (
                <SidebarNavItem
                  key={item.href}
                  href={item.href}
                  icon={item.icon}
                  label={t(item.labelKey)}
                  current={isCurrent(item.href)}
                />
              ))}
            </SidebarSection>
          )
        })}
      </SidebarBody>

      {auth.user && (
        <SidebarFooter>
          <UserMenu
            user={auth.user}
            labels={{ settings: t('common.settings'), signOut: t('common.sign_out') }}
            onSignOut={() => router.delete('/users/sign_out')}
          />
        </SidebarFooter>
      )}
    </Sidebar>
  )
}
